import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Building2, Bell, Check, Palette, Plus, Users } from "lucide-react";
import { toast } from "sonner";
import { useTheme } from "@/components/ThemeProvider";
import { API_BASE } from "@/lib/utils";
import { getAuthHeaders } from "@/lib/auth-fetch";
import { commonTimezones, DEFAULT_TIMEZONE } from "@/lib/timezone";
import { ONBOARDING_DOCUMENT_TITLE, resetLoginDocumentBranding } from "@/lib/tenant-brand";
import { apiErrorDetail, uiDebugLog } from "@/lib/ui-debug-log";

const INVITE_ROLES = ["Admin", "Marketing", "Onsite", "Finance"];

function emptyInviteRow() {
  return { id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, email: "", role: "Admin" };
}

const steps = [
  { key: "welcome", label: "Welcome", icon: Building2 },
  { key: "org", label: "Organization", icon: Building2 },
  { key: "brand", label: "Theme", icon: Palette },
  { key: "notifications", label: "Notifications", icon: Bell },
  { key: "team", label: "Team", icon: Users },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { setTheme } = useTheme();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [org, setOrg] = useState({
    workspaceName: "",
    legalEntity: "",
    timezone: DEFAULT_TIMEZONE,
    theme: "dark",
  });
  const [notifications, setNotifications] = useState({
    alertEmails: "",
    slackWebhookUrl: "",
  });
  const [inviteRows, setInviteRows] = useState([emptyInviteRow()]);
  const [sentInvites, setSentInvites] = useState([]);
  const [sendingInviteId, setSendingInviteId] = useState("");

  useEffect(() => {
    resetLoginDocumentBranding(ONBOARDING_DOCUMENT_TITLE);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/admin/workspace/onboarding`, { headers: getAuthHeaders() });
        if (!res.ok) return;
        const data = await res.json();
        if (data?.completed) {
          navigate("/admin", { replace: true });
          return;
        }
        if (data?.org) setOrg((prev) => ({ ...prev, ...data.org }));
        if (data?.notifications) setNotifications((prev) => ({ ...prev, ...data.notifications }));
      } catch {
        // continue with defaults
      }
    })();
  }, [navigate]);

  useEffect(() => {
    const t = String(org.theme || "").toLowerCase();
    if (t === "light" || t === "dark") setTheme(t, true);
  }, [org.theme, setTheme]);

  const saveProgress = async (patch, { completed = false } = {}) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/workspace/onboarding`, {
        method: "PATCH",
        headers: { "content-type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ ...patch, completed }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = apiErrorDetail(data, "Could not save onboarding");
        uiDebugLog("Onboarding.jsx:saveProgress", "save failed", { code: res.status, msg, completed }, "H");
        throw new Error(msg);
      }
      uiDebugLog("Onboarding.jsx:saveProgress", "save ok", { step, completed }, "H");
      if (completed) {
        toast.success("Workspace ready", { description: "You can create your first event anytime." });
        const billingUrl = sessionStorage.getItem("aivoryems.postOnboardingBillingUrl");
        if (billingUrl) {
          sessionStorage.removeItem("aivoryems.postOnboardingBillingUrl");
          window.location.href = billingUrl;
          return;
        }
        navigate("/admin", { replace: true });
      }
    } catch (err) {
      uiDebugLog("Onboarding.jsx:saveProgress", "save exception", { message: err?.message || String(err) }, "H");
      toast.error(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const next = async () => {
    if (step === 1 && !String(org.workspaceName || "").trim()) {
      toast.error("Workspace name is required");
      return;
    }
    await saveProgress({ step: step + 1, org, notifications });
    if (step < steps.length - 1) {
      setStep((s) => s + 1);
      return;
    }
    await saveProgress({ org, notifications }, { completed: true });
  };

  const skip = () => saveProgress({ org, notifications }, { completed: true });

  const sendInvite = async (rowId) => {
    const row = inviteRows.find((r) => r.id === rowId);
    const email = String(row?.email || "").trim().toLowerCase();
    if (!email) {
      toast.error("Enter an email address");
      return;
    }
    setSendingInviteId(rowId);
    try {
      const res = await fetch(`${API_BASE}/api/admin/team/invites`, {
        method: "POST",
        headers: { "content-type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ email, role: row.role }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Invite failed");
      setSentInvites((prev) => [...prev, { email, role: row.role, sentAt: new Date().toISOString() }]);
      setInviteRows((prev) => {
        const next = prev.filter((r) => r.id !== rowId);
        return next.length ? next : [emptyInviteRow()];
      });
      toast.success("Invitation sent", { description: email });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSendingInviteId("");
    }
  };

  const addInviteRow = () => setInviteRows((prev) => [...prev, emptyInviteRow()]);

  const updateInviteRow = (rowId, patch) => {
    setInviteRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, ...patch } : r)));
  };

  return (
    <div className="min-h-screen bg-[var(--bg-base)] px-4 py-10 md:py-16" data-testid="workspace-onboarding">
      <div className="max-w-[720px] mx-auto">
        <div className="text-overline">Workspace setup</div>
        <h1 className="font-heading text-3xl md:text-4xl mt-2 tracking-tight font-medium">
          {step === 0 && "Welcome to AivoryEMS"}
          {step === 1 && "Organization"}
          {step === 2 && "Theme & appearance"}
          {step === 3 && "Notifications"}
          {step === 4 && "Invite your team"}
        </h1>
        <p className="mt-3 text-sm text-[var(--text-secondary)] leading-relaxed">
          {step === 0 &&
            "A few quick steps to configure your workspace before you create events. You can change everything later under Settings."}
          {step === 1 && "This name appears in the admin header, workspace switcher, and on invoices."}
          {step === 2 && "Choose the default admin theme. Event pages can override this per event."}
          {step === 3 && "Where should we send operational alerts (optional)?"}
          {step === 4 && "Add colleagues now or skip and invite them later from Settings → Team."}
        </p>

        <div className="mt-8 flex gap-2 flex-wrap">
          {steps.map((s, i) => (
            <span
              key={s.key}
              className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 border ${
                i === step ? "border-[var(--primary)] text-[var(--primary)]" : "border-[var(--border-default)] text-[var(--text-tertiary)]"
              }`}
            >
              {s.label}
            </span>
          ))}
        </div>

        <div className="mt-8 surface p-6 space-y-4">
          {step === 0 ? (
            <ul className="text-sm text-[var(--text-secondary)] space-y-2 list-disc pl-5">
              <li>Organization name and timezone</li>
              <li>Admin theme (light or dark)</li>
              <li>Alert email for registrations and billing</li>
              <li>Optional team invites</li>
            </ul>
          ) : null}

          {step === 1 ? (
            <>
              <Field label="Workspace name">
                <input
                  className="input-dark w-full"
                  value={org.workspaceName}
                  onChange={(e) => setOrg((p) => ({ ...p, workspaceName: e.target.value }))}
                  placeholder="e.g. Aivory Lab"
                />
              </Field>
              <Field label="Legal entity (optional)">
                <input
                  className="input-dark w-full"
                  value={org.legalEntity}
                  onChange={(e) => setOrg((p) => ({ ...p, legalEntity: e.target.value }))}
                />
              </Field>
              <Field label="Timezone">
                <select className="input-dark w-full" value={org.timezone} onChange={(e) => setOrg((p) => ({ ...p, timezone: e.target.value }))}>
                  {commonTimezones.map((z) => (
                    <option key={z.value} value={z.value}>
                      {z.label}
                    </option>
                  ))}
                </select>
              </Field>
            </>
          ) : null}

          {step === 2 ? (
            <div className="flex flex-wrap gap-3">
              {["dark", "light"].map((t) => (
                <button
                  key={t}
                  type="button"
                  className={`btn-secondary capitalize ${org.theme === t ? "ring-1 ring-[var(--primary)]" : ""}`}
                  onClick={() => setOrg((p) => ({ ...p, theme: t }))}
                >
                  {t}
                </button>
              ))}
            </div>
          ) : null}

          {step === 3 ? (
            <>
              <Field label="Alert email">
                <input
                  type="email"
                  className="input-dark w-full font-mono text-sm"
                  value={notifications.alertEmails}
                  onChange={(e) => setNotifications((p) => ({ ...p, alertEmails: e.target.value }))}
                  placeholder="finance@yourcompany.com"
                />
              </Field>
              <Field label="Slack webhook (optional)">
                <input
                  className="input-dark w-full font-mono text-sm"
                  value={notifications.slackWebhookUrl}
                  onChange={(e) => setNotifications((p) => ({ ...p, slackWebhookUrl: e.target.value }))}
                  placeholder="https://hooks.slack.com/..."
                />
              </Field>
            </>
          ) : null}

          {step === 4 ? (
            <div className="space-y-5">
              <p className="text-sm text-[var(--text-secondary)]">
                Invite colleagues to your workspace. You can add more rows and send each invite individually.
              </p>
              <div className="space-y-3">
                {inviteRows.map((row) => (
                  <div key={row.id} className="grid sm:grid-cols-[1fr_140px_auto] gap-3 items-end">
                    <Field label="Email">
                      <input
                        type="email"
                        className="input-dark w-full"
                        value={row.email}
                        placeholder="colleague@company.com"
                        onChange={(e) => updateInviteRow(row.id, { email: e.target.value })}
                      />
                    </Field>
                    <Field label="Role">
                      <select
                        className="input-dark w-full"
                        value={row.role}
                        onChange={(e) => updateInviteRow(row.id, { role: e.target.value })}
                      >
                        {INVITE_ROLES.map((role) => (
                          <option key={role} value={role}>
                            {role}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <button
                      type="button"
                      className="btn-secondary text-sm h-[42px] whitespace-nowrap"
                      disabled={!row.email.trim() || sendingInviteId === row.id}
                      onClick={() => sendInvite(row.id)}
                    >
                      {sendingInviteId === row.id ? "Sending…" : "Send invite"}
                    </button>
                  </div>
                ))}
              </div>
              <button type="button" className="btn-ghost text-sm inline-flex items-center gap-2" onClick={addInviteRow}>
                <Plus size={14} /> Add another colleague
              </button>
              {sentInvites.length ? (
                <div className="border border-[var(--border-default)] p-4 space-y-2">
                  <div className="text-overline">Sent invitations</div>
                  <ul className="space-y-2">
                    {sentInvites.map((inv) => (
                      <li key={`${inv.email}-${inv.sentAt}`} className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
                        <Check size={14} className="text-[var(--primary)] shrink-0" />
                        <span className="font-mono text-[var(--text-primary)]">{inv.email}</span>
                        <span className="text-[var(--text-tertiary)]">· {inv.role}</span>
                        <span className="ml-auto text-[10px] uppercase tracking-widest text-[var(--primary)]">Sent</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
          <button type="button" className="btn-ghost text-sm" onClick={skip} disabled={saving}>
            Skip for now
          </button>
          <button type="button" className="btn-primary text-sm" onClick={next} disabled={saving}>
            {saving ? "Saving…" : step === steps.length - 1 ? "Finish" : "Continue"} <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-overline block mb-2">{label}</span>
      {children}
    </label>
  );
}
